'use client';
import {
  Box,
  ButtonBase,
  GlobalStyles,
  Grid,
  IconButton,
  Stack,
  SwipeableDrawer,
  SxProps,
  Theme,
  useMediaQuery,
} from '@mui/material';
import { getClientCookie, setClientCookie } from '@repo/utils';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Logo } from '../../assets/logo';
import { LinkPendingIcon } from '../../components/linkPendingIcon';
import { MaterialIcon } from '../../components/materialIcon';
import { MaterialIconType } from '../../components/materialIcon/types';
import { ClientOnlyTransition } from '../../components/transition';
import {
  BRAND_GRADIENTS,
  BRAND_TONE,
  BrandTone,
  COLORS,
  MOTION,
  REDUCED_MOTION_NO_TRANSITION,
  SHADOWS,
} from '../../constants';
import { MenuLayoutContext } from './context';

export * from './context';

export type MenuLayoutTab = {
  icon: MaterialIconType;
  label: string;
  href: string;
  /** Path prefixes that highlight the tab, e.g. pages opened from it. Defaults to the href */
  match?: string[];
  /** Runs before navigating, e.g. to reset a flow's state */
  onClick?: () => void;
  /** The screen's main action: a raised filled circle, meant for the middle slot */
  primary?: boolean;
};

// Bottom tab bar on phones, like customer-mobile; the safe area is added below it
const TAB_BAR_HEIGHT = '3.5rem';

// Same look as customer-mobile's tab bar: Cocogoose 10px labels and filled icons, violet when active, grey otherwise
const tabSx = (active: boolean): SxProps<Theme> => ({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 0.25,
  p: 0,
  border: 'none',
  background: 'none',
  fontFamily: (theme) => theme.typography.fontFamily,
  fontSize: '10px',
  fontWeight: 400,
  color: active ? COLORS.text.brand : COLORS.text.light3,
  textDecoration: 'none',
  WebkitTapHighlightColor: 'transparent',
});

// Filled circle lifted above the bar, ringed in the bar's color so it reads as cut out of it
const primaryIconSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 48,
  height: 48,
  mt: '-20px',
  borderRadius: '50%',
  border: `4px solid ${COLORS.surface.raised}`,
  backgroundColor: COLORS.brand.main,
  color: COLORS.text.inverted,
  boxShadow: SHADOWS.card,
};

// Inline: apps' unlayered .material-symbols-outlined rule (FILL 0) beats anything in MUI's @layer
const FILLED_ICON_STYLE = { fontVariationSettings: "'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24" };

/**
 * Renders the main layout of the app, with a side Menu and main content.
 *
 * @param {React.ReactNode} children - The content to be rendered within the layout.
 * @param {React.ReactElement} menu - The menu to be displayed within the drawer.
 * @param {MenuLayoutTab[]} [tabs] - Phones only: a bottom tab bar that replaces the top bar and its menu.
 * @param {BrandTone} [tone] - Brand of the background wash, the app's NEXT_PUBLIC_BRAND_TONE by default.
 * @return {React.JSX.Element} The main layout of the app.
 */
export const MenuLayout = ({
  children,
  menu,
  tabs,
  tone = BRAND_TONE,
}: {
  children: React.ReactNode;
  menu: React.ReactNode;
  tabs?: MenuLayoutTab[];
  tone?: BrandTone;
}): React.JSX.Element => {
  const collapsedMenuCookie = getClientCookie('collapsedMenu');
  const [collapsedMenu, setCollapsedMenu] = useState(collapsedMenuCookie === 'true' ? true : false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const isDesktop = useMediaQuery('(min-width: 1200px)', { noSsr: true });
  const menuToggleRef = useRef<HTMLButtonElement>(null);
  const pathname = usePathname();

  useEffect(() => {
    setOpenDrawer(false);
  }, [pathname]);

  const menuContext = useMemo(
    () => ({
      closeMenu: () => setOpenDrawer(false),
      collapsedMenu,
      setCollapsedMenu: (newState: boolean) => {
        (setCollapsedMenu(newState), setClientCookie('collapsedMenu', newState.toString()));
      },
    }),
    [collapsedMenu],
  );

  return (
    <Grid
      data-testid="menu-layout"
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100dvh',
        height: '100%',
        background: BRAND_GRADIENTS.wash(tone),
        // The wash stays at the top of the window while the page scrolls
        backgroundAttachment: 'fixed',
      }}
    >
      <Box
        component="a"
        href="#main-content"
        data-testid="menu-layout-skip-link"
        sx={{
          position: 'absolute',
          top: 8,
          left: 8,
          zIndex: (theme) => theme.zIndex.tooltip + 1,
          px: 2,
          py: 1,
          borderRadius: 2,
          background: COLORS.surface.raised,
          color: 'primary.main',
          fontWeight: 600,
          textDecoration: 'none',
          transform: 'translateY(-200%)',
          '&:focus': { transform: 'none' },
        }}
      >
        Saltar al contenido
      </Box>
      <Stack
        component="header"
        sx={{
          // With tabs, phones open the menu from "Ver más" instead
          display: {
            xs: tabs ? 'none' : 'flex',
            md: 'flex',
            lg: 'none',
          },
          height: '3.5rem',
          width: '100%',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 1,
          background: COLORS.surface.raised,
        }}
      >
        <IconButton
          aria-label={openDrawer ? 'Cerrar menú' : 'Abrir menú'}
          aria-expanded={openDrawer}
          data-testid="menu-layout-toggle"
          ref={menuToggleRef}
          onClick={() => setOpenDrawer(!openDrawer)}
        >
          <MaterialIcon icon="menu" sx={{ color: COLORS.text.brand }} />
        </IconButton>
        <Box component={Link} href={'/'} aria-label="Directo, inicio" sx={{ display: 'flex', alignItems: 'center' }}>
          <Logo height={22} decorative />
        </Box>
      </Stack>
      <Grid sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', height: '100%' }}>
        {/* Client only: drawer variant and width come from noSsr media queries and the collapsedMenu cookie */}
        <ClientOnlyTransition type="fadeLeft">
          {/* Swipe left closes it like a native drawer. Swipe to open stays off: the left edge is iOS back navigation */}
          <SwipeableDrawer
            data-testid="menu-layout-drawer"
            disableSwipeToOpen
            sx={{
              width: { xs: 0, lg: collapsedMenu && isDesktop ? 90 : 280 },
              flexShrink: 0,
              // The slot and the paper resize together, so the content slides with the menu instead of jumping
              transition: isDesktop ? `width ${MOTION.medium}ms ${MOTION.easeOut}` : 'unset',
              ...REDUCED_MOTION_NO_TRANSITION,
              '& .MuiDrawer-paper': {
                transition: isDesktop ? `width ${MOTION.medium}ms ${MOTION.easeOut} !important` : 'unset',
                ...REDUCED_MOTION_NO_TRANSITION,
                overflow: 'visible',
                width: collapsedMenu && isDesktop ? 90 : 280,
                // height: { xs: 'calc(100dvh - 3rem)', lg: '100dvh' },
                minHeight: '100dvh',
                boxSizing: 'border-box',
                // mt: { xs: '3rem', lg: 0 },
              },
            }}
            variant={isDesktop ? 'persistent' : 'temporary'}
            anchor="left"
            open={isDesktop ? true : openDrawer}
            onClose={() => {
              setOpenDrawer(false);
              menuToggleRef?.current?.focus();
            }}
            onOpen={() => setOpenDrawer(true)}
            // Unmounted while closed, like the plain drawer: nothing to swipe open, so no need to keep it in the page
            ModalProps={{ keepMounted: false }}
            slotProps={{
              paper: {
                ...{ 'data-testid': 'menu-layout-drawer-paper' },
                // Temporary (mobile) drawer paper is a modal dialog; give it a name
                'aria-label': isDesktop ? undefined : 'Menú principal',
                // Borderless, lifted by the card shadow like the section cards
                sx: { borderRadius: 0, border: 'none', boxShadow: SHADOWS.card },
              },
            }}
          >
            <Box
              component="nav"
              aria-label="Menú principal"
              sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
            >
              <MenuLayoutContext value={menuContext}>{menu}</MenuLayoutContext>
            </Box>
          </SwipeableDrawer>
        </ClientOnlyTransition>
        <Grid
          component="main"
          id="main-content"
          tabIndex={-1}
          sx={{
            outline: 'none',
            flex: 1,
            display: 'flex',
            minHeight: { xs: tabs ? '100dvh' : 'calc(100dvh - 3.5rem)', md: 'calc(100dvh - 3.5rem)', lg: '100dvh' },
            // Room for the fixed tab bar, so the end of the page and its pinned buttons stay above it
            pb: tabs ? { xs: `calc(${TAB_BAR_HEIGHT} + env(safe-area-inset-bottom))`, md: 0 } : undefined,
          }}
        >
          {children}
        </Grid>
      </Grid>
      {tabs && (
        // Fixed widgets (the WhatsApp button) read it to sit above the tab bar on phones
        <GlobalStyles
          styles={(theme) => ({
            [theme.breakpoints.down('md')]: {
              ':root': { '--menu-layout-tab-bar': `calc(${TAB_BAR_HEIGHT} + env(safe-area-inset-bottom))` },
            },
          })}
        />
      )}
      {tabs && (
        <Box
          component="nav"
          aria-label="Accesos rápidos"
          data-testid="menu-layout-tabs"
          sx={{
            display: { xs: 'flex', md: 'none' },
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: (theme) => theme.zIndex.appBar,
            height: `calc(${TAB_BAR_HEIGHT} + env(safe-area-inset-bottom))`,
            pb: 'env(safe-area-inset-bottom)',
            background: COLORS.surface.raised,
            borderTop: `1px solid ${COLORS.border.faint}`,
          }}
        >
          {tabs.map(({ icon, label, href, match = [href], onClick, primary }) => {
            const active = match.some((prefix) => pathname?.startsWith(prefix));
            return (
              // ButtonBase: apps' unlayered `a:not(.MuiButtonBase-root) { color: inherit }` would override a plain link's
              // colors. No ripple, like the app's tab buttons
              <ButtonBase
                key={href}
                component={Link}
                href={href}
                disableRipple
                aria-current={active ? 'page' : undefined}
                onClick={onClick}
                sx={tabSx(active || Boolean(primary))}
              >
                {primary ? (
                  <Box aria-hidden="true" sx={primaryIconSx}>
                    <LinkPendingIcon icon={icon} style={FILLED_ICON_STYLE} />
                  </Box>
                ) : (
                  <LinkPendingIcon icon={icon} style={FILLED_ICON_STYLE} />
                )}
                {label}
              </ButtonBase>
            );
          })}
        </Box>
      )}
    </Grid>
  );
};
